'use client';
import React, { useState } from 'react';
import styles from './auth.module.css';
import Image from 'next/image';
import Link from 'next/link';
import { signIn, useSession } from 'next-auth/react';
import { redirect, useRouter } from 'next/navigation';

export default function Auth({ onClose }: { onClose?: () => void }) {
	const { data: session } = useSession();
	if (session?.user && !onClose) return redirect('/');
	const router = useRouter();

	const [email, setEmail] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [error, setError] = useState('');

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError('');

		try {
			const { error } = await signIn('http-email', {
				email,
				redirect: false,
				redirectTo:
					window.location.pathname === '/auth' ? '/' : window.location.href,
			});

			if (error) {
				router.push(`/auth/error?error=${encodeURIComponent(error)}`);
				return;
			}
			setIsSubmitted(true);
		} catch (err) {
			setError('Something went wrong. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={`${!onClose && styles.auth}`}>
			<div
				className={`${styles['auth-card']} ${
					!onClose && styles['auth-card-hover']
				}`}
			>
				<div className={styles.logoContainer}>
					<Image src={'/side-logo.svg'} alt={''} width={200} height={44} />
				</div>

				{!isSubmitted ? (
					<>
						<h1 className={styles.title}>Welcome back</h1>
						<p className={styles.subtitle}>
							Sign in to your account using your email
						</p>

						<form onSubmit={handleSubmit} className={styles.form}>
							<div className={styles.inputGroup}>
								<label htmlFor='email' className={styles.label}>
									Email address
								</label>
								<input
									id='email'
									type='email'
									value={email}
									onChange={(e) => setEmail(e.target.value.toLocaleLowerCase())}
									placeholder='name@example.com'
									required
									className={styles.input}
								/>
							</div>

							{error && <div className={styles.error}>{error}</div>}

							<button
								type='submit'
								className={styles.button}
								disabled={isSubmitting || !email}
							>
								{isSubmitting ? 'Sending...' : 'Send Magic Link'}
							</button>
						</form>

						<p className={styles.helpText}>
							We'll send you a magic link to your email.
							<br />
							No password needed!
						</p>
					</>
				) : (
					<div className={styles.successMessage}>
						<div className={styles.checkmark}>✓</div>
						<h2 className={styles.successTitle}>Check your email</h2>
						<p className={styles.successText}>
							We've sent a magic link to <strong>{email}</strong>
						</p>
						<div style={{ margin: '0 0 2rem' }}>
							<p className={styles.successSubtext}>
								Click the link in the email to sign in to your account.
							</p>
							{onClose && (
								<p className={styles.successSubtext}>
									Verify your account and refresh this page
								</p>
							)}
						</div>

						{onClose ? (
							<button
								onClick={() => {
									setEmail('');
									onClose();
								}}
								className={styles.secondaryButton}
							>
								Close
							</button>
						) : (
							<Link
								href={'/'}
								onClick={() => {
									setEmail('');
								}}
								className={styles.secondaryButton}
							>
								Back to home
							</Link>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
